<template>
  <form>
    <div class="image-container mb-3" v-if="previewPath">
      <img :src="previewPath" alt="Uploaded Image Preview" />
    </div>
    <div class="form-group">
      <div ref="dashboardContainer"></div>
    </div>
    <!-- <v-btn :disabled="disabled" @click.prevent="confirmUpload" class="btn btn-primary btn-block mb-2">Confirm upload</v-btn> -->
  </form>
</template>

<script>
import HeicToJpeg from "@/plugins/uppyHeicToJpeg";
import Uppy from "@uppy/core";
import { AwsS3Multipart, uploadedKey } from "@/utils/uppyS3Multipart";
// import XHRUpload from "@uppy/xhr-upload";
// import S3Upload from "@uppy/aws-s3-multipart";
// import companion from "@uppy/companion";
let uuid = require("uuid");
import Dashboard from "@uppy/dashboard";
import ImageEditor from "@uppy/image-editor";
import { IMAGE_EDITOR_DOCUMENT, growWhileEditing } from "@/utils/uppyImageEditor";
import { nativeCameraOptions } from "@/utils/uppyCamera";
// import Form from "@uppy/form";
import Russian from "@uppy/locales/lib/ru_RU";
import English from "@uppy/locales/lib/en_US";
import Compressor from "@uppy/compressor";
import { s3PublicUrl } from "@/utils/s3PublicUrl";

import "@uppy/core/css/style.css";
import "@uppy/dashboard/css/style.css";
import "@uppy/image-editor/css/style.css";
import { RASTER_IMAGE_AND_DOCUMENT_TYPES } from "@/utils/uploadFileTypes";

export default {
  props: {
    maxFileSizeInBytes: {
      type: Number,
      required: true,
    },
    width: {
      required: true,
    },
    height: {
      required: true,
    },
    editedClient: {
      type: Object,
    },
    theme: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      payload: null,
      previewPath: null,
      disabled: true,
    };
  },
  mounted() {
    this.instantiateUppy();
  },
  beforeDestroy() {
    this.uppy.destroy();
  },
  methods: {
    instantiateUppy() {
      let self = this;
      this.uppy = new Uppy({
        debug: false,
        locale: this.$i18n.locale === "ru" ? Russian : English,
        // Без автозагрузки: файл правят в редакторе и отправляют кнопкой «Загрузить»
        // (utils/uppyImageEditor.js).
        autoProceed: false,
        allowMultipleUploadBatches: true,
        width: this.width,
        height: this.height,
        restrictions: {
          maxFileSize: this.maxFileSizeInBytes,
          minNumberOfFiles: 1,
          maxNumberOfFiles: 10,
          allowedFileTypes: RASTER_IMAGE_AND_DOCUMENT_TYPES,
        },
      })
        .use(HeicToJpeg, { quality: 0.92 })
        .use(Dashboard, {
          ...nativeCameraOptions(),
          theme: this.theme,
          inline: true,
          width: this.width,
          height: this.height,
          target: this.$refs.dashboardContainer,
          browserBackButtonClose: true,
          proudlyDisplayPoweredByUppy: false,
        })
        // Сжатие в браузере — ДО отправки в S3. Здесь оно решает не вес, а формат.
        //
        // Yandex Vision OCR принимает только JPEG/PNG/PDF (в api это
        // Yandex::OcrInput::SUPPORTED_TYPES). Замер по проду за 7 дней: из 21
        // отказа распознавания 14 — `Can't decode image`, и все они webp
        // (пересылки из мессенджеров) либо HEIC (айфоны). Пережатие в JPEG прямо
        // на клиенте убирает причину, а не последствие.
        //
        // mimeType (ед. ч.) — та самая строка, ради которой всё это ставится.
        // По умолчанию у compressorjs mimeType: "auto", то есть тип выхода равен
        // типу входа: webp на входе — webp на выходе. Сам по себе он переводит в
        // JPEG только PNG тяжелее convertSize. Опция mimeTypes (мн. ч.)
        // не читается ни @uppy/compressor, ни compressorjs —
        // формат она не правит; convertTypes/convertSize при явном mimeType
        // недостижимы, поэтому их здесь нет.
        //
        // PDF, doc и docx проходят нетронутыми: @uppy/compressor отсеивает файл
        // до вызова compressorjs проверкой file.type.startsWith("image/")
        // (lib/index.js), и ни на какой список типов эта проверка не завязана.
        //
        // HEIC/TIFF заранее переводит в JPEG плагин FT-206. Компрессор
        // получает уже декодируемый файл и сохраняет прежнюю политику размера.
        //
        // Рамка НЕ 1920×1080 и quality НЕ 0.7, как у фото машин, потому что
        // здесь снимают документы. width/height при resize: "none" вписывают
        // кадр в прямоугольник: вертикальный снимок 3024×4032 садится в 810×1080
        // — по странице паспорта это ~130 dpi, на которых мелкий шрифт и MRZ
        // рассыпаются. maxWidth/maxHeight только уменьшают и не трогают пропорции:
        // тот же снимок остаётся 1920×2560. quality 0.85 — артефакты JPEG
        // садятся ровно на контуры букв.
        .use(ImageEditor, IMAGE_EDITOR_DOCUMENT)
        .use(Compressor, {
          quality: 0.85,
          limit: 5,
          mimeType: "image/jpeg",
          maxWidth: 2560,
          maxHeight: 2560,
          locale: {
            strings: {
              compressingImages: this.$i18n.locale === "ru" ? "Сжимаем изображение..." : "Compressing image...",
              compressedX: this.$i18n.locale === "ru" ? "Сжато %{size} байт" : "Compressed %{size} bytes",
            },
          },
        })
        .use(AwsS3Multipart, {
          limit: 5,
          companionEndpoint: `${this.$store.getters.getApiUrl}/clients`, // FT-018
        });
      growWhileEditing(this.uppy, this.height);
      this.uppy.on("complete", (event) => {
        event.successful.forEach((element) => {
          if (element !== undefined) {
            // console.log(element);
            self.axios
              .post(
                `/api/v1/clients_files/${self.$route.params.id}`,
                {
                  clients_files: {
                    name: uploadedKey(element),
                    url: s3PublicUrl(uploadedKey(element)),
                    original_name: element.name,
                    document_type: element.type,
                  },
                },
                {
                  headers: {
                    Authorization: self.$store.getters.getAuthToken,
                  },
                }
              )
              .then((response) => {
                // Эмитим полный объект файла от бэкенда (включая client_id и
                // presigned_url), чтобы распознавание документа работало сразу
                // после загрузки, без перезагрузки страницы.
                this.$emit("updateClientFiles", response.data);
              })
              .catch((error) => {
                this.setError(error, this.$t("errors.error"));
              });
          }
        });
      });
    },
    uploadYandex(files) {
      console.log(files);
    },
    updatePreviewPath({ path }) {
      this.previewPath = path;

      return this;
    },
    resetUploader() {
      this.uppy.cancelAll();
      this.disabled = true;

      return this;
    },
    confirmUpload() {
      if (this.payload) {
        this.disabled = true;
        this.axios
          .post("/store", { file: this.payload })
          .then(({ data }) => {
            this.updatePreviewPath(data).resetUploader();
            this.$notify("Файлы загружены!", "Вы успешно загрузили файлы!", "success", 5000);
          })
          .catch((err) => {
            console.error(err);

            this.resetUploader();
          });
      } else {
        this.$notify("Ошибка при загрузке!", this.error, "error", 5000);
      }
    },
  },
};
</script>

<style scoped>
.image-container {
  height: 150px;
  width: 150px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: auto;
  margin-left: auto;
}

.image-container>img {
  width: inherit;
  height: inherit;
}
</style>
